/**
 * QChatMessages Component (v9.0 - PHASE 4.1 Compact Chat)
 * Readable text, minimal spacing, proper markdown
 *
 * v9.0 Updates (PHASE 4.1):
 * - Compact 20px avatar (institutional grade)
 * - Uses QAI design tokens
 * - Reduced padding and spacing
 *
 * v8.0 Updates (UI Polish - Feature Flagged):
 * - Larger fonts for better readability (15px → 17px)
 * - Brighter text colors (#E2E8F0 → #F1F5F9)
 * - Feature flag: PREMIUM_TYPOGRAPHY for safe rollback
 *
 * v7.0 Updates (Task 2.3/3.3):
 * - Display conversation continuity indicator
 * - Display response source badge (LIVE/CACHED/FALLBACK)
 */

import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import QActionCard from './QActionCard';
import QActionChips from './QActionChips';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import {
  QAI_TYPE_SCALE,
  QAI_FONTS,
  QAI_COLORS,
  QAI_SPACING
} from '../../theme/qaiDesignTokens';

// ═══════════════════════════════════════════════════════════════════════════
// TYPOGRAPHY CONFIGURATION - PHASE 4.1: Compact Chat Design
// ═══════════════════════════════════════════════════════════════════════════

const TYPOGRAPHY = {
  // Premium (new) - PHASE 4.1: Compact 20px avatar
  // FONT FIX: Updated bodySize from 14px to 1rem (16px) to match chat input
  premium: {
    bodySize: '1rem',                 // 16px - matches chat input for consistency
    bodyColor: QAI_COLORS.text.primary,
    lineHeight: 1.5,
    avatarSize: 20,                   // PHASE 4.1: Compact 20px
    avatarRadius: '5px',
    avatarFontSize: QAI_TYPE_SCALE['2xs'],  // 10px
  },
  // Original (fallback)
  // FONT FIX: Updated bodySize from 13px to 0.9375rem (15px) for better readability
  original: {
    bodySize: '0.9375rem',            // 15px - improved readability
    bodyColor: QAI_COLORS.text.secondary,
    lineHeight: 1.5,
    avatarSize: 20,                   // PHASE 4.1: Compact 20px
    avatarRadius: '5px',
    avatarFontSize: QAI_TYPE_SCALE['2xs'],  // 10px
  }
};

// Get current typography based on feature flag
const getTypography = () => {
  return FEATURE_FLAGS.PREMIUM_TYPOGRAPHY ? TYPOGRAPHY.premium : TYPOGRAPHY.original;
};

// Task 3.3: Response source indicator configuration
const SOURCE_CONFIG = {
  LIVE: {
    color: '#22C55E',
    bgColor: 'rgba(34, 197, 94, 0.1)',
    label: 'LIVE',
    icon: '🟢'
  },
  CACHED: {
    color: '#EAB308',
    bgColor: 'rgba(234, 179, 8, 0.1)',
    label: 'CACHED',
    icon: '🟡'
  },
  FALLBACK: {
    color: '#EF4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    label: 'FALLBACK',
    icon: '🔴'
  }
};

const QChatMessages = ({
  messages = [],
  onActionCard,
  onActionChip,
  isLoading = false,
  hasConversation = false  // Task 2.3: Show conversation continuity indicator
}) => {
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: QAI_SPACING[3],  // 8px - PHASE 4.1 compact
        py: QAI_SPACING[2],  // 6px
        display: 'flex',
        flexDirection: 'column',
        gap: QAI_SPACING[3], // 8px - compact message spacing
        backgroundColor: QAI_COLORS.background.primary,
        '&::-webkit-scrollbar': { width: '3px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.08)', borderRadius: '2px' }
      }}
    >
      {/* Task 2.3: Conversation continuity indicator */}
      {hasConversation && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: '4px',
            mb: '4px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              px: '10px',
              py: '3px',
              borderRadius: '12px',
              backgroundColor: 'rgba(39, 181, 109, 0.08)',
              border: '1px solid rgba(39, 181, 109, 0.15)'
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#27B56D',
                animation: 'pulse 2s infinite'
              }}
            />
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#27B56D',
                fontWeight: 500,
                letterSpacing: '0.02em'
              }}
            >
              Conversation active
            </Typography>
          </Box>
        </Box>
      )}

      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          onActionCard={onActionCard}
          onActionChip={onActionChip}
        />
      ))}

      {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', py: 0, my: 0 }}>
          <QAvatar />
          <TypingDots />
        </Box>
      )}

      <div ref={messagesEndRef} style={{ height: 0, margin: 0, padding: 0 }} />
    </Box>
  );
};

/** Q Avatar - PHASE 4.1: Compact 20px avatar using design tokens */
const QAvatar = ({ isError = false }) => {
  const typo = getTypography();

  return (
    <Box
      sx={{
        width: typo.avatarSize,
        height: typo.avatarSize,
        minWidth: typo.avatarSize,
        borderRadius: typo.avatarRadius,
        background: isError
          ? `linear-gradient(135deg, ${QAI_COLORS.semantic.bearish} 0%, #DC2626 100%)`
          : `linear-gradient(135deg, ${QAI_COLORS.legacy.signalBuy} 0%, ${QAI_COLORS.semantic.bullish} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isError
          ? '0 1px 4px rgba(239, 68, 68, 0.2)'
          : '0 1px 4px rgba(39, 181, 109, 0.2)',
        flexShrink: 0,
        mt: '1px'  // PHASE 4.1: Reduced from 2px
      }}
    >
      <Typography
        sx={{
          color: QAI_COLORS.text.primary,
          fontSize: typo.avatarFontSize,
          fontWeight: 800,
          fontFamily: QAI_FONTS.primary,
          lineHeight: 1
        }}
      >
        Q
      </Typography>
    </Box>
  );
};

/** Typing Dots - visible */
const TypingDots = () => (
  <Box sx={{ display: 'flex', gap: '3px', ml: '4px' }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          backgroundColor: '#27B56D',
          animation: 'pulse 1s infinite',
          animationDelay: `${i * 0.15}s`,
          '@keyframes pulse': {
            '0%, 60%, 100%': { opacity: 0.3 },
            '30%': { opacity: 1 }
          }
        }}
      />
    ))}
  </Box>
);

/** Task 3.3: Response Source Badge */
const ResponseSourceBadge = ({ source }) => {
  if (!source) return null;

  const config = SOURCE_CONFIG[source] || SOURCE_CONFIG.LIVE;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        px: '6px',
        py: '2px',
        borderRadius: '4px',
        backgroundColor: config.bgColor,
        border: `1px solid ${config.color}20`,
        mt: '4px'
      }}
    >
      <Typography
        component="span"
        sx={{ fontSize: '0.75rem', lineHeight: 1 }}
      >
        {config.icon}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: config.color,
          letterSpacing: '0.03em',
          textTransform: 'uppercase'
        }}
      >
        {config.label}
      </Typography>
    </Box>
  );
};

/** Premium Welcome Card - PHASE 4.1: Compact Institutional Layout */
const PremiumWelcomeCard = ({ message, onActionChip }) => {
  const typo = getTypography();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: '10px', sm: '12px' },  // PHASE 4.1: Reduced padding
        px: { xs: '10px', sm: '12px' }
      }}
    >
      {/* Q.ai Brand - PHASE 4.1: Compact inline branding */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',  // Changed to row layout
          gap: 1,
          mb: { xs: '8px', sm: '10px' }  // Reduced margin
        }}
      >
        {/* Q Icon - PHASE 4.1: Compact 28px */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '6px',
            background: 'linear-gradient(135deg, #27B56D 0%, #2DD881 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(39, 181, 109, 0.25)'
          }}
        >
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '0.875rem',
              fontWeight: 800,
              fontFamily: "'Inter', -apple-system, sans-serif",
              lineHeight: 1
            }}
          >
            Q
          </Typography>
        </Box>
        {/* Q.ai Brand Text - PHASE 4.1: Compact inline */}
        <Typography
          sx={{
            color: '#FFFFFF',
            fontSize: '1.125rem',  // Reduced from 1.5rem/1.75rem
            fontWeight: 700,
            fontFamily: "'Inter', -apple-system, sans-serif",
            letterSpacing: '-0.02em',
            '& .accent': {
              color: '#27B56D'
            }
          }}
        >
          Q<span className="accent">.ai</span>
        </Typography>
      </Box>

      {/* Welcome Text - PHASE 4.1: Compact centered text */}
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: { xs: '260px', sm: '280px' },
          mb: { xs: '10px', sm: '12px' }  // Reduced margin
        }}
      >
        <Box
          sx={{
            color: typo.bodyColor,
            fontSize: { xs: '0.8125rem', sm: '0.875rem' },  // Reduced font size
            lineHeight: 1.4,  // Tighter line height
            fontFamily: "'Inter', sans-serif",
            '& p': { margin: 0, marginBottom: '4px', '&:last-child': { marginBottom: 0 } },
            '& strong': { color: '#FFFFFF', fontWeight: 600 }
          }}
        >
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </Box>
      </Box>

      {/* Action Chips - PHASE 4.1: Compact width */}
      {message.showActionChips && (
        <Box sx={{ width: '100%', maxWidth: { xs: '260px', sm: '280px' } }}>
          <QActionChips onAction={onActionChip} disabled={false} variant="grid" />
        </Box>
      )}
    </Box>
  );
};

/** Message Row - Typography controlled by feature flag */
const MessageBubble = ({ message, onActionCard, onActionChip }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isError = isAssistant && message.content?.toLowerCase().includes('error');
  const showSourceBadge = isAssistant && !message.isStreaming && !message.showActionChips && message.source;
  const isPremiumWelcome = message.isPremiumWelcome && FEATURE_FLAGS.PREMIUM_WELCOME;

  const typo = getTypography();

  // Render premium welcome card for welcome messages when flag is enabled
  if (isPremiumWelcome) {
    return <PremiumWelcomeCard message={message} onActionChip={onActionChip} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        flexDirection: isUser ? 'row-reverse' : 'row'
      }}
    >
      {isAssistant && <QAvatar isError={isError} />}

      <Box sx={{ maxWidth: '85%' }}>
        {/* Text bubble */}
        <Box
          sx={{
            display: 'inline-block',
            backgroundColor: isUser ? 'rgba(39,181,109,0.08)' : 'transparent',
            borderRadius: isUser ? '10px' : 0,
            padding: isUser ? '6px 12px' : 0,
            border: isUser ? '1px solid rgba(39,181,109,0.12)' : 'none'
          }}
        >
          {isAssistant ? (
            <Box
              sx={{
                color: typo.bodyColor,
                fontSize: typo.bodySize,
                lineHeight: typo.lineHeight,
                fontFamily: "'Inter', sans-serif",
                // Markdown styling - Claude style
                '& p': {
                  margin: 0,
                  marginBottom: '8px',
                  '&:last-child': { marginBottom: 0 }
                },
                '& strong': {
                  color: '#FFFFFF',
                  fontWeight: 700
                },
                '& ul, & ol': {
                  margin: '8px 0',
                  paddingLeft: '20px'
                },
                '& li': {
                  marginBottom: '4px',
                  '&:last-child': { marginBottom: 0 }
                },
                '& code': {
                  backgroundColor: 'rgba(39,181,109,0.1)',
                  color: '#27B56D',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '0.875em',
                  fontFamily: "'JetBrains Mono', monospace"
                },
                '& pre': {
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  margin: '8px 0',
                  overflow: 'auto'
                },
                '& blockquote': {
                  paddingLeft: '12px',
                  borderLeft: '3px solid #27B56D',
                  margin: '8px 0',
                  color: '#94A3B8'
                }
              }}
            >
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </Box>
          ) : (
            <Typography
              sx={{
                color: typo.bodyColor,
                fontSize: typo.bodySize,
                lineHeight: typo.lineHeight,
                fontFamily: "'Inter', sans-serif"
              }}
            >
              {message.content}
            </Typography>
          )}
        </Box>

        {/* Task 3.3: Response Source Badge */}
        {showSourceBadge && (
          <ResponseSourceBadge source={message.source} />
        )}

        {/* Action Cards */}
        {message.actionCards?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: '6px' }}>
            {message.actionCards.map((card) => (
              <QActionCard key={card.id} {...card} onClick={onActionCard} />
            ))}
          </Box>
        )}

        {/* Action Chips */}
        {message.showActionChips && (
          <Box sx={{ mt: '8px' }}>
            <QActionChips onAction={onActionChip} disabled={false} variant="grid" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default QChatMessages;
